import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { FaPlusCircle } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import Table from "../../../Utils/Table/Table";
import socket from "../../../Utils/socketClient";
import * as Config from "../../../Utils/Config";
import Swal from "sweetalert2";
import { FiEdit, FiTrash2, FiUnlock, FiLock } from "react-icons/fi";
import {
  AddResearch,
  getResearch,
  GetsymbolExchange,
  DeleteResearch,
  EditResearch,
  UpdatStatus,
} from "../../../Services/Admin/Addmin";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { getUserFromToken } from "../../../Utils/TokenVerify";

const initialFormData = {
  researchType: "",
  coin: "",
  price: "",
  targetPrice: "",
  stopLoss: "",
  entryReason: "",
  note: "",
  status: "Open",
  type: "buy",
};

const formFields = [
  { label: "Entry Price", name: "price", type: "number" },
  { label: "Target Price", name: "targetPrice", type: "number" },
  { label: "Stop Loss", name: "stopLoss", type: "number" },
];

const Research = () => {
  const TokenData = getUserFromToken();
  const user_id = TokenData?.user_id;

  const [rowsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [researchData, setResearchData] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [Symbols, setSymbols] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState("All");
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (name === "coin" && value) {
        const selectedCoinPrice = livePrices[value.toLowerCase()];
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          ...(selectedCoinPrice && { price: selectedCoinPrice }),
        }));
      } else if (name === "researchType") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          coin: "",
        }));
        setSelectedCoin(null);
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    },
    [livePrices]
  );

  useEffect(() => {
    const handleData = (data) => {
      const symbol = data?.data?.ticker?.toLowerCase();
      const price = Number(data?.data?.Mid_Price);

      if (symbol && !isNaN(price)) {
        setLivePrices((prev) => ({
          ...prev,
          [symbol]: price.toFixed(4),
        }));
      }
    };

    socket.on("receive_data_forex", handleData);
    return () => socket.off("receive_data_forex", handleData);
  }, []);

  const validatePrices = useCallback(() => {
    const { price, targetPrice, stopLoss, type } = formData;
    const entryPrice = parseFloat(price);
    const target = parseFloat(targetPrice);
    const stop = parseFloat(stopLoss);

    if (!entryPrice || !target || !stop) return true;

    if (type === "buy") {
      if (target <= entryPrice) {
        toast.error("Target Price should be greater than Entry Price for buy orders.");
        return false;
      }
      if (stop >= entryPrice) {
        toast.error("Stop Loss should be less than Entry Price for buy orders.");
        return false;
      }
    } else if (type === "sell") {
      if (target >= entryPrice) {
        toast.error("Target Price should be less than Entry Price for sell orders.");
        return false;
      }
      if (stop <= entryPrice) {
        toast.error("Stop Loss should be greater than Entry Price for sell orders.");
        return false;
      }
    }

    return true;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!formData.coin) {
        toast.error("Please select a coin/pair.");
        return;
      }

      if (!validatePrices()) return;

      try {
        const response = editMode
          ? await EditResearch({ ...formData, id: editId })
          : await AddResearch({ ...formData, user_id });

        if (response?.status || response) {
          toast.success(`Research ${editMode ? "updated" : "added"} successfully!`);
          setShowModal(false);
          setFormData(initialFormData);
          setSelectedCoin(null);
          setEditMode(false);
          setRefresh((prev) => !prev);
        } else {
          toast.error(`Failed to ${editMode ? "update" : "add"} research.`);
        }
      } catch (error) {
        toast.error("Something went wrong.");
      }
    },
    [formData, validatePrices, editMode, editId, user_id]
  );

  const fetchResearchData = useCallback(async () => {
    if (!user_id) return;
    try {
      const response = await getResearch(user_id);
      if (response?.data) setResearchData(response.data);
    } catch (error) {
      console.error("Error fetching research data:", error);
    }
  }, [user_id]);

  const fetchSymbols = useCallback(async () => {
    if (!user_id) return;
    try {
      const response = await GetsymbolExchange({ exch: formData.researchType });
      setSymbols(response?.data || []);
    } catch (error) {
      console.error("Error fetching symbols:", error);
    }
  }, [user_id, formData.researchType]);

  useEffect(() => {
    if (user_id) {
      fetchSymbols();
      fetchResearchData();
    }
  }, [user_id, refresh, fetchSymbols, fetchResearchData]);

  const filteredSymbols = useMemo(() => {
    return Symbols.filter(
      (symbol) =>
        symbol?.exch_seg?.toLowerCase() === formData?.researchType?.toLowerCase()
    );
  }, [Symbols, formData.researchType]);

  const handleEdit = useCallback((data) => {
    setFormData(data);
    setEditMode(true);
    setEditId(data._id);

    if (data.researchType === "Indian") {
      setSelectedCoin({ label: data.coin, value: data.coin });
    }

    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    async (id) => {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to delete this entry?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (!result.isConfirmed) return;

      const response = await DeleteResearch({ id });

      if (response) {
        toast.success("Research deleted successfully!");
        setRefresh((prev) => !prev);
      } else {
        toast.error("Failed to delete research. Please try again.");
      }
    },
    []
  );

  const handleToggleStatus = useCallback(
    async (id, status) => {
      const updatedStatus = status === "Open" ? "Close" : "Open";

      const result = await Swal.fire({
        title: `Are you sure?`,
        text: `Do you want to ${updatedStatus.toLowerCase()} this entry?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: `Yes, ${updatedStatus} it!`,
      });

      if (!result.isConfirmed) return;

      const response = await UpdatStatus({ id, status: updatedStatus });

      if (response?.status) {
        toast.success(`Research ${updatedStatus}d successfully!`);
        setRefresh((prev) => !prev);
      } else {
        toast.error("Failed to update research status. Please try again.");
      }
    },
    []
  );


  const columns = useMemo(
    () => [
      { Header: "Coin", accessor: "researchType" },
      { Header: "Type", accessor: "coin" },
      { Header: "Price", accessor: "price" },
      { Header: "Target", accessor: "targetPrice" },
      { Header: "Stop Loss", accessor: "stopLoss" },
      {
        Header: "Reason",
        accessor: "entryReason",
        Cell: ({ cell }) => {
          const text = cell.value || "";
          const shortText = text.length > 15 ? text.substring(0, 15) + "..." : text;
          return <span title={text}>{shortText}</span>;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell }) => {
          const isOpen = cell.value === "Open";
          return (
            <span
              style={{
                backgroundColor: isOpen ? "#d1fae5" : "#e5e7eb",
                color: isOpen ? "#065f46" : "#374151",
                padding: "6px 12px",
                borderRadius: "10px",
                fontSize: "12px",
                fontWeight: 600,
                display: "inline-block",
                minWidth: "70px",
                textAlign: "center",
              }}
            >
              {cell.value}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ cell }) => (
          <div style={{ display: "flex", gap: "8px" }}>


            <Button
              variant="primary"
              size="sm"
              onClick={() => handleEdit(cell.row)}
              className="d-flex align-items-center gap-1"
            >
              <FiEdit /> Edit
            </Button>

            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDelete(cell.row._id)}
              className="d-flex align-items-center gap-1"
            >
              <FiTrash2 /> Delete
            </Button>

            <Button
              variant={cell.row.status === "Open" ? "warning" : ""}
              size="sm"
              onClick={() => handleToggleStatus(cell.row._id, cell.row.status)}
              className="d-flex align-items-center text-black gap-1"
            >
              {cell.row.status === "Open" ? <FiLock /> : <FiUnlock />}
              {cell.row.status === "Open" ? "Close" : "Open"}
            </Button>

          </div>
        ),
      },
    ],
    [handleEdit, handleDelete, handleToggleStatus]
  );

  const loadOptions = useCallback(
    async (inputText, callback) => {
      if (!inputText || inputText.length < 2) {
        callback([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.post(`${Config.base_url}symbols`, {
          page: "1",
          limit: "100",
          zebu_token: inputValue,
          type: selectedOption,
        });

        const options =
          response.data?.data?.map((coin) => ({
            label: coin.tradesymbol,
            value: coin.symbol,
            id: coin._id,
          })) || [];

        callback(options);
      } catch (error) {
        console.error("Error fetching coins:", error);
        callback([]);
      } finally {
        setIsLoading(false);
      }
    },
    [inputValue, selectedOption]
  );

  const handleAsyncSelectChange = useCallback((option) => {
    setSelectedCoin(option);
    setFormData((prev) => ({
      ...prev,
      coin: option?.value || "",
      serviceId: option?.id || "",
    }));
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setFormData(initialFormData);
    setSelectedCoin(null);
    setEditMode(false);
    setEditId(null);
  }, []);

  const handleAddResearch = useCallback(() => {
    setShowModal(true);
    setFormData(initialFormData);
    setSelectedCoin(null);
    setEditMode(false);
    setEditId(null);
  }, []);

  return (
    <div className="container-fluid mt-5">
      <div className="row">
        <div className="col-lg-12">
          <div className="card transaction-table shadow-sm" style={{ background: "#f8f9fa", minHeight: "100vh", }}>
            <div className="container-fluid p-4">
              <div
                className="d-flex justify-content-between align-items-center w-100 "
              >

                <h4 className="card-title" >📈 Trade Research</h4>
                <Button onClick={handleAddResearch} variant="primary">
                  <FaPlusCircle className="me-2" /> Add Research
                </Button>
              </div>

              <div className="card w-100 mt-3">
                <div className="card-body p-0">
                  <div
                    className="table-responsive"
                    style={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Table columns={columns} data={researchData} rowsPerPage={rowsPerPage} />
                  </div>
                </div>
              </div>
            </div>

            <Modal show={showModal} onHide={handleModalClose} size="lg " centered>
               <div className="modal-left-border">
              <Modal.Header closeButton>
                <Modal.Title>{editMode ? "Edit Research" : "Add Research"}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <label className="form-label" style={{ color: "#000", fontWeight: "600" }}>
                          Research Type
                        </label>

                        <Form.Select
                          name="researchType"
                          value={formData.researchType}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="CRYPTO">Crypto</option>
                          <option value="FOREX">Forex</option>
                          <option value="Indian">Indian</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {formData.researchType === "Indian" ? (
                      <>
                        <Col md={2}>
                          <Form.Group>
                            <label className="form-label" style={{ color: "#000", fontWeight: "600" }}>
                              Exchange
                            </label>
                            <Form.Select
                              value={selectedOption}
                              onChange={(e) => setSelectedOption(e.target.value)}
                              required
                            >
                              <option value="">Select Exchange</option>
                              <option value="All">All</option>
                              <option value="Cash">Cash</option>
                              <option value="F&O">F&O</option>
                              <option value="Commodity">Commodity</option>
                              <option value="Currency">Currency</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Stocks</Form.Label>
                            <AsyncSelect
                              cacheOptions
                              loadOptions={loadOptions}
                              onInputChange={setInputValue}
                              onChange={handleAsyncSelectChange}
                              value={selectedCoin}
                              placeholder="Search & select coin..."
                              isLoading={isLoading}
                              isClearable
                              noOptionsMessage={({ inputValue }) =>
                                inputValue.length < 2
                                  ? "Type at least 2 characters to search"
                                  : "No options found"
                              }
                            />
                          </Form.Group>
                        </Col>
                      </>
                    ) : (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Coin/Pair</Form.Label>
                          <Form.Select
                            name="coin"
                            value={formData.coin}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Coin/Pair</option>
                            {filteredSymbols.length > 0 ? (
                              filteredSymbols.map((symbol, index) => (
                                <option key={index} value={symbol.symbol}>
                                  {symbol.symbol}
                                </option>
                              ))
                            ) : (
                              <option value="">No symbols available</option>
                            )}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>

                  <Row className="mb-3">
                    {formFields.map((field, idx) => (
                      <Col md={6} key={idx}>
                        <Form.Group>
                          <Form.Label>
                            {field.label}
                            {field.name === "price" &&
                              formData.coin &&
                              livePrices[formData.coin.toLowerCase()] && (
                                <span className="text-success ms-2">
                                  (Live: {livePrices[formData.coin.toLowerCase()]})
                                </span>
                              )}
                          </Form.Label>
                          <Form.Control
                            type={field.type}
                            name={field.name}
                            value={formData[field.name]}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    ))}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="buy" style={{ color: "green" }}>
                            Buy
                          </option>
                          <option value="sell" style={{ color: "red" }}>
                            Sell
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Entry Reason</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="entryReason"
                      value={formData.entryReason}
                      onChange={handleInputChange}
                      rows={2}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Note</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="note"
                      value={formData.note}
                      onChange={handleInputChange}
                      rows={2}
                      required
                    />
                  </Form.Group>

                  <div className="text-end">
                    <Button variant="secondary" onClick={handleModalClose}>
                      Cancel
                    </Button>{" "}
                    <Button variant="primary" type="submit">
                      {editMode ? "Update" : "Submit"}
                    </Button>
                  </div>
                </Form>
              </Modal.Body>
              </div>
            </Modal>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Research;